import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Check, Download, FileText, Sparkles, Trash2, Upload } from 'lucide-react';
import {
  useDeleteOrderDocument,
  useDownloadOrderDocument,
  useExtractOrderDocument,
  useOrderDocuments,
  useUploadOrderDocument,
  type ExtractDeliveryTicketResult,
  type OrderDocument,
} from '../../features/order-documents/api';
import {
  shouldShowTicketCard,
  type TicketApplyDraft,
} from '../../features/order-documents/deliveryTicket';
import DeliveryTicketReviewModal from '../DeliveryTicketReviewModal/DeliveryTicketReviewModal';

export interface DeliveryTicketCardProps {
  orderId: string;
  orderItemId: string;
  loadNumber: string | null;
  status: string;
  current: { quantity: number; ticketNumber: string; poNumber: string; deliveryDate: string };
  canEdit: boolean;
  onApply: (draft: TicketApplyDraft) => void;
}

const mapExtractError = (e: any): string => {
  const code = e?.response?.data?.error;
  if (code === 'AI_READER_NOT_CONFIGURED') return 'The AI reader is not configured on the server.';
  if (code === 'UNSUPPORTED_DOCUMENT_TYPE') return 'This file type cannot be read.';
  if (code === 'EXTRACTION_FAILED') return 'Could not read this ticket — try a clearer scan.';
  return 'Failed to read the ticket.';
};

export default function DeliveryTicketCard({
  orderId,
  orderItemId,
  loadNumber,
  status,
  current,
  canEdit,
  onApply,
}: DeliveryTicketCardProps) {
  const { data } = useOrderDocuments(orderId);
  const upload = useUploadOrderDocument(orderId);
  const extract = useExtractOrderDocument(orderId);
  const remove = useDeleteOrderDocument(orderId);
  const download = useDownloadOrderDocument(orderId);

  const documents: OrderDocument[] = data?.documents ?? [];
  const ticket = documents.find(
    (d) => d.type === 'DELIVERY_TICKET' && d.orderItemId === orderItemId
  );
  const extraction = ticket?.extractedData ?? null;

  const [extracting, setExtracting] = useState(false);
  const [result, setResult] = useState<ExtractDeliveryTicketResult | null>(null);
  const [showReview, setShowReview] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const busy = upload.isPending || extracting;

  // Show on assigned/picked-up/delivered loads, or any load that already has a
  // ticket. Hidden on earlier/other statuses to avoid clutter.
  if (!shouldShowTicketCard(status, Boolean(ticket))) return null;

  const runExtract = async (documentId: string) => {
    setExtracting(true);
    try {
      const res = await extract.mutateAsync(documentId);
      setResult(res);
      setShowReview(true);
      if (!res.delivered.loadNumberMatches) {
        toast.warn('The ticket load number does not match this load — review carefully.');
      }
    } catch (e) {
      toast.error(mapExtractError(e));
    } finally {
      setExtracting(false);
    }
  };

  const onFile = async (file: File) => {
    try {
      const res = await upload.mutateAsync({ file, type: 'DELIVERY_TICKET', orderItemId });
      const newDoc: OrderDocument | undefined = res?.document;
      toast.success('Ticket uploaded — reading with AI…');
      if (newDoc?.id) await runExtract(newDoc.id);
    } catch (e: any) {
      const code = e?.response?.data?.error;
      toast.error(
        code === 'FILE_TOO_LARGE'
          ? 'That file is too large (15 MB max).'
          : code === 'UNSUPPORTED_FILE_TYPE'
            ? 'That file type is not supported.'
            : 'Failed to upload the ticket.'
      );
    }
  };

  // Prefer the fresh extract response (avoids a refetch race), else the stored doc.
  const modalExtraction = result?.extraction ?? extraction ?? null;
  const modalPages = result?.previewPages ?? ticket?.previewPages ?? [];

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <FileText size={16} />
        <strong>Delivery ticket</strong>
        {ticket && extraction && (
          <span style={{ color: '#16a34a', display: 'inline-flex', gap: 4 }}>
            <Check size={14} /> read
          </span>
        )}
      </div>

      {!ticket && (
        <div className="info-banner info-banner--warning" style={{ marginTop: 8 }}>
          {status === 'DELIVERED'
            ? 'This load is delivered. Upload the delivery ticket to confirm the details.'
            : 'Upload the signed delivery ticket to confirm delivery. Once you review and apply it, the load is marked Delivered.'}
        </div>
      )}

      {!ticket && canEdit && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Upload delivery ticket: drag and drop a file here, or click to browse"
          onClick={() => {
            if (!busy) fileInputRef.current?.click();
          }}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !busy) {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (!busy) setIsDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragging(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            if (busy) return;
            const f = e.dataTransfer.files?.[0];
            if (f) void onFile(f);
          }}
          style={{
            marginTop: 8,
            border: `2px dashed ${isDragging ? '#2563eb' : '#cbd5e1'}`,
            background: isDragging ? '#eff6ff' : '#f8fafc',
            borderRadius: 10,
            padding: '18px 14px',
            textAlign: 'center',
            cursor: busy ? 'default' : 'pointer',
            color: '#475569',
            transition: 'border-color 120ms, background 120ms',
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            style={{ display: 'none' }}
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onFile(f);
              e.target.value = '';
            }}
          />
          <Upload size={18} style={{ opacity: 0.7 }} className={busy ? 'spin' : ''} />
          <div style={{ marginTop: 6, fontSize: 13 }}>
            {busy ? (
              'Uploading & reading with AI…'
            ) : (
              <>
                <strong>Drag &amp; drop</strong> a delivery ticket here, or{' '}
                <strong>click to browse</strong>
              </>
            )}
          </div>
          <div className="muted tiny" style={{ marginTop: 2 }}>
            PDF, JPG, PNG or WEBP · up to 15 MB
          </div>
        </div>
      )}

      {ticket && (
        <div
          style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
        >
          <span className="muted tiny" style={{ flex: 1, minWidth: 160 }}>
            {ticket.fileName}
          </span>
          {extraction ? (
            <button className="btn btn--primary" onClick={() => setShowReview(true)}>
              Review &amp; apply
            </button>
          ) : (
            canEdit && (
              <button
                className="btn btn--ghost"
                onClick={() => runExtract(ticket.id)}
                disabled={extracting}
                title="Read the ticket with AI"
              >
                <Sparkles size={14} className={extracting ? 'spin' : ''} />{' '}
                {extracting ? 'Reading…' : 'Read with AI'}
              </button>
            )
          )}
          <button
            className="btn btn--ghost"
            onClick={() => download.mutate({ documentId: ticket.id, fileName: ticket.fileName })}
          >
            <Download size={14} /> Download
          </button>
          {canEdit && (
            <button
              className="btn btn--ghost"
              onClick={() => {
                if (window.confirm('Remove this delivery ticket?')) remove.mutate(ticket.id);
              }}
              title="Remove ticket"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      )}

      {showReview && modalExtraction && modalPages.length > 0 && (
        <DeliveryTicketReviewModal
          extraction={modalExtraction}
          previewPages={modalPages}
          loadNumber={loadNumber}
          current={current}
          canEdit={canEdit}
          onApply={onApply}
          onClose={() => setShowReview(false)}
        />
      )}
    </div>
  );
}
